import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TvCard from '@/components/feature/TvCard';
import JsonLd from '@/components/base/JsonLd';
import { tvSeriesList, categories } from '@/mocks/tvSeries';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { categoryId } = useParams<{ categoryId: string }>();

  const [activeCategory, setActiveCategory] = useState(categoryId || 'guochan');

  const currentCategory = useMemo(
    () => categories.find((c) => c.id === activeCategory),
    [activeCategory]
  );

  const filteredSeries = useMemo(
    () => tvSeriesList.filter((s) => s.categoryId === activeCategory),
    [activeCategory]
  );

  const breadcrumbItems = [
    { label: t('breadcrumb.category'), to: '/category/guochan' },
    { label: currentCategory?.name || '' },
  ];

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 四虎影视大全`,
    description: `四虎影视大全${currentCategory?.name || ''}频道，提供最新${currentCategory?.name || ''}电视剧全集在线观看，免费全集一网打尽。`,
    url: `https://www.sihutv.com/category/${activeCategory}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '四虎影视大全',
      url: 'https://www.sihutv.com/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.sihutv.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: currentCategory?.name || '',
          item: `https://www.sihutv.com/category/${activeCategory}`,
        },
      ],
    },
  };

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <JsonLd data={categoryPageSchema} />
      <Navbar />
      <div className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <main>
          {/* Category Header */}
          <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                {t('category.title')}
              </h1>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Series List */}
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  {currentCategory?.name}
                </h2>
                <span className="text-sm text-foreground-500">
                  {t('category.totalResults', { count: filteredSeries.length })}
                </span>
              </div>

              {filteredSeries.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredSeries.map((series) => (
                    <TvCard key={series.id} series={series} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">{t('category.noResults')}</p>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
}